// Minimal DOM helper. Everything is created as a node and appended, so no
// user-supplied string is ever parsed as HTML — the same guarantee the
// server-side html`` template gives, by construction rather than by escaping.

export function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value === null || value === undefined || value === false) continue;
    if (key === 'class') node.className = value;
    else if (key === 'dataset') Object.assign(node.dataset, value);
    else node.setAttribute(key, value === true ? '' : String(value));
  }

  for (const child of [].concat(children)) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return node;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

// Icons are built as nodes like everything else. document.createElement would
// produce an HTMLUnknownElement for <svg> and render nothing, and parsing an
// icon from a string would give up the guarantee above for no gain.
export function icon(d, { size = 16, className = 'icon' } = {}) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  for (const [key, value] of Object.entries({
    viewBox: '0 0 24 24', width: size, height: size,
    'aria-hidden': 'true', focusable: 'false', class: className,
  })) svg.setAttribute(key, String(value));

  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', d);
  svg.append(path);
  return svg;
}

export function clear(node) {
  node.replaceChildren();
}

let dialogSeq = 0;

// Every dialog in the backend is built the same way: a <form> whose first
// child is an h2.dialog-title. Naming that heading and pointing the dialog at
// it is what turns "Dialog" into "Reihe bearbeiten, Dialog" in a screen
// reader, and doing it here means the five call sites cannot drift apart.
//
// Focus return is the browser's job for a modal <dialog>, but only if the
// element is still in the document when it closes — hence removing on 'close'
// rather than immediately.
export function openDialog(form, { className = 'pub-dialog pub-dialog-wide' } = {}) {
  const dialog = el('dialog', { class: className });
  const heading = form.querySelector('.dialog-title');
  if (heading) {
    dialogSeq += 1;
    heading.id = heading.id || `dialog-title-${dialogSeq}`;
    dialog.setAttribute('aria-labelledby', heading.id);
  }

  dialog.append(form);
  document.body.append(dialog);
  dialog.showModal();
  dialog.addEventListener('close', () => dialog.remove());

  // Clicking the backdrop closes, the same escape Esc already offers — so this
  // loses no more than the key does, and both are why a draft is saveable from
  // the first slide on.
  //
  // A click on the backdrop targets the <dialog> itself, because the backdrop is
  // its pseudo-element rather than a node. Comparing against the box is what
  // separates it from a click that lands on the padding: the dialog has padding
  // and a click there is a click inside.
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    const outside = event.clientX < box.left || event.clientX > box.right
      || event.clientY < box.top || event.clientY > box.bottom;
    if (outside) dialog.close();
  });

  return dialog;
}

// The event form, which is not a dialog.
//
// A <dialog> traps focus and lays a backdrop over the page — exactly what you
// do not want while entering an event, because the calendar behind it is the
// thing you are entering it *against*: which Fridays are already taken, what
// the month looks like. So this is a plain aside beside the content, the page
// keeps scrolling, and the calendar stays clickable.
//
// `aria-modal` is deliberately absent rather than false: the panel genuinely
// is not modal, and saying so by not claiming otherwise is the accurate
// signal. Escape still closes, because that is what people press.
export function openPanel(form, { onClose } = {}) {
  const panel = el('aside', { class: 'side-panel', role: 'dialog', tabindex: '-1' });
  const heading = form.querySelector('.dialog-title');
  if (heading) {
    dialogSeq += 1;
    heading.id = heading.id || `panel-title-${dialogSeq}`;
    panel.setAttribute('aria-labelledby', heading.id);
  }

  // The scrim exists on phones only, where the panel is a sheet over the page
  // and there is nothing beside it to keep in view. On a desktop it would grey
  // out the calendar the panel is there to work against.
  const scrim = el('div', { class: 'side-scrim' });

  panel.append(form);
  document.body.append(scrim, panel);
  // Synchronously, and the entrance is a CSS animation rather than a class
  // toggled on the next frame. requestAnimationFrame does not fire while a tab
  // is not compositing — so the version that waited for a frame left the panel
  // closed in any background tab, which is not an animation bug but a "the
  // feature does not open" bug. A keyframe runs on insertion and needs nobody.
  document.body.dataset.panel = 'open';

  // Beside the calendar means the calendar's own band, not the whole window.
  // CSS cannot ask where the grid is, so this measures it once and on resize —
  // no scroll listener, because the panel is fixed and the grid sits near the
  // top of a page you open it from.
  //
  // Only where the panel is docked beside the column. Narrower than that it
  // fills the height it has, and on a phone it is a sheet.
  const measure = () => {
    const beside = window.matchMedia('(min-width: 82rem)').matches;
    const grid = document.querySelector('.cal-grid');
    if (!grid || !beside) {
      panel.style.removeProperty('--panel-top');
      panel.style.removeProperty('--panel-height');
      return;
    }
    const box = grid.getBoundingClientRect();
    panel.style.setProperty('--panel-top', `${Math.max(box.top, 0)}px`);
    panel.style.setProperty('--panel-height', `${Math.round(box.height)}px`);
  };
  measure();
  window.addEventListener('resize', measure);

  const escape = (event) => { if (event.key === 'Escape') close(); };
  document.addEventListener('keydown', escape);
  scrim.addEventListener('click', () => close());

  let closed = false;
  function close() {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', escape);
    window.removeEventListener('resize', measure);
    // The day the panel was about stops being marked with it.
    document.dispatchEvent(new CustomEvent('tba:event-date', { detail: { date: null } }));
    delete document.body.dataset.panel;
    panel.remove();
    scrim.remove();
    onClose?.();
  }

  // Same surface as openDialog so a form can be handed either container.
  panel.close = close;
  return panel;
}

export function fragment(children) {
  const frag = document.createDocumentFragment();
  for (const child of [].concat(children)) {
    if (child === null || child === undefined || child === false) continue;
    frag.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return frag;
}
